import { routes } from "@/constants/url";
import { supabase } from "@/lib/supabase";
import { useToastMessage } from "@backpac/monica";
import { Button, Input, Typography } from "@backpac/monica";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

// NOTE: If a user tries to sign up with an email that is already in use, the verification email will be not sent.
// @see https://supabase.com/docs/guides/auth/auth-identity-linking#can-you-sign-up-with-email-if-already-using-oauth
export function SignupEmailVerifyPage() {
  const [otp, setOTP] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { error: errorToast, info: infoToast } = useToastMessage();
  const [verifyInFlight, setVerifyInFlight] = useState(false);

  const email = searchParams.get("email") ?? "";

  const resend = async () => {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
    });

    if (error != null) {
      errorToast("Failed to resend verification code");
    } else {
      infoToast("A verification email has been resent 💌");
    }
  };

  const verify = async () => {
    setVerifyInFlight(true);
    try {
      const response = await supabase.auth.verifyOtp({
        type: "signup",
        email,
        token: otp,
      });
      if (response.error) {
        errorToast("Failed to verify email");
      } else {
        navigate(routes.WORKSPACE_NAVIGATOR.root, { replace: true });
      }
    } finally {
      setVerifyInFlight(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography asChild size="xl" font="unbounded">
        <h1>Verify your email address</h1>
      </Typography>

      <Typography asChild size="sm">
        <p>
          Code sent to: {email}
          <br />
          Enter the code below.
        </p>
      </Typography>

      <form
        className="flex flex-col gap-6"
        id="email-verify"
        onSubmit={(e) => {
          e.preventDefault();
          verify();
        }}
      >
        <Input autoFocus={true} type="text" placeholder="6-digit code" onChange={(e) => setOTP(e.target.value)} />

        <Button
          form="email-verify"
          disabled={otp.length < 6 || verifyInFlight}
          type="submit"
          size="lg"
          className="w-full"
        >
          Verify Email Address
        </Button>
      </form>

      <div className="flex items-center gap-1">
        <Typography size="xs" weight={600}>
          Not seeing the email?
        </Typography>
        <Button variant="ghost" className="underline px-0" size="sm" onClick={resend}>
          Resend
        </Button>
      </div>

      <Typography size="xs" className="text-red-500">
        If your email is already registered, the verification email won't be sent.
        <br />
        In that case, please sign in instead.
      </Typography>
    </div>
  );
}
